/**
 * Catalogues de référence — types et validation
 *
 * Le catalogue est **une donnée, pas du code** : trois fichiers JSON versionnés
 * (`donnees/catalogue-*.json`) que l'on peut relire, corriger et compléter sans
 * recompiler. Une périodicité réglementaire change, un constructeur préconise
 * autre chose : c'est une modification de fichier, pas de programme.
 *
 * En échange, un fichier éditable à la main peut être faux. Ce module le valide
 * intégralement au chargement : clés dupliquées, RRULE hors du sous-ensemble
 * supporté, condition mal formée, référence légale manquante sur une obligation.
 * Mieux vaut un démarrage qui refuse un fichier douteux qu'une échéance
 * silencieusement absente pendant deux ans.
 */
import { ANCRAGES } from './vacances.js';
import { valideExpression, variablesDe } from './expression.js';
import { analyseRrule } from './rrule.js';
/** Les huit catégories de l'application, alignées sur la contrainte de la base. */
export const CATEGORIES = [
    'sante',
    'maison',
    'chauffage',
    'assainissement',
    'ecole',
    'travaux',
    'administratif',
    'courses',
];
export const LIBELLE_CATEGORIE = {
    sante: 'Santé',
    maison: 'Maison',
    chauffage: 'Chauffage & énergie',
    assainissement: 'Assainissement',
    ecole: 'École & enfants',
    travaux: 'Travaux',
    administratif: 'Administratif',
    courses: 'Courses',
};
/**
 * D'où vient l'obligation. L'interface l'affiche, et l'utilisateur doit pouvoir
 * désactiver une bonne pratique sans toucher aux obligations légales.
 */
export const ORIGINES = ['legale', 'constructeur', 'bonne_pratique'];
export const LIBELLE_ORIGINE = {
    legale: 'obligation légale',
    constructeur: 'préconisation constructeur',
    bonne_pratique: 'bonne pratique',
};
/**
 * Date d'amorçage d'une règle. `aujourdhui` est un dernier recours : quand on
 * s'en sert, la première échéance est marquée « estimée, à ajuster » plutôt que
 * présentée comme une date connue.
 */
export const AMORCES = ['dernier_entretien', 'date_installation', 'date_achat', 'aujourdhui'];
export const TYPES_CARACTERISTIQUE = ['nombre', 'texte', 'booleen', 'choix'];
// ── Santé ───────────────────────────────────────────────────────────────────
export const ORIGINES_SANTE = ['obligatoire', 'recommande_assurance_maladie', 'bonne_pratique'];
export const LIBELLE_ORIGINE_SANTE = {
    obligatoire: 'obligatoire',
    recommande_assurance_maladie: "recommandé par l'Assurance maladie",
    bonne_pratique: 'bonne pratique',
};
// ── Validation ──────────────────────────────────────────────────────────────
export class ErreurCatalogue extends Error {
}
const RE_CLE = /^[a-z][a-z0-9_]*$/;
const RE_GABARIT = /\{([a-z][a-z0-9_]*)\}/g;
/** Remplace les gabarits `{cle}` d'une RRULE par les caractéristiques déclarées. */
export function appliqueGabarits(rrule, valeurs) {
    return rrule.replace(RE_GABARIT, (_, nom) => {
        const v = valeurs[nom];
        if (v === undefined || v === null)
            throw new ErreurCatalogue(`gabarit « ${nom} » sans valeur`);
        return String(v);
    });
}
export function gabaritsDe(rrule) {
    return [...rrule.matchAll(RE_GABARIT)].map((m) => m[1]);
}
function exigeCle(valeur, ou) {
    if (typeof valeur !== 'string' || !RE_CLE.test(valeur)) {
        throw new ErreurCatalogue(`${ou} : clé invalide « ${String(valeur)} » (attendu min_uscule_snake)`);
    }
    return valeur;
}
function exigeTexte(valeur, ou) {
    if (typeof valeur !== 'string' || !valeur.trim())
        throw new ErreurCatalogue(`${ou} : texte requis`);
    return valeur;
}
function exigeCategorie(valeur, ou) {
    if (!CATEGORIES.includes(valeur)) {
        throw new ErreurCatalogue(`${ou} : catégorie inconnue « ${String(valeur)} »`);
    }
    return valeur;
}
function exigeRappels(valeur, ou) {
    if (!Array.isArray(valeur) || valeur.some((n) => !Number.isInteger(n) || n < 0)) {
        throw new ErreurCatalogue(`${ou} : rappels attendus sous forme de jours entiers positifs`);
    }
    return valeur;
}
function valideRrule(rrule, ou, caracteristiques) {
    for (const gabarit of gabaritsDe(rrule)) {
        if (!caracteristiques.has(gabarit)) {
            throw new ErreurCatalogue(`${ou} : gabarit « {${gabarit}} » sans caractéristique correspondante`);
        }
    }
    // On valide la règle en substituant une valeur neutre aux gabarits.
    const sonde = rrule.replace(RE_GABARIT, '1');
    try {
        analyseRrule(sonde);
    }
    catch (err) {
        throw new ErreurCatalogue(`${ou} : ${err.message}`);
    }
}
function valideCondition(condition, ou, connues) {
    try {
        valideExpression(condition);
    }
    catch (err) {
        throw new ErreurCatalogue(`${ou} : condition invalide — ${err.message}`);
    }
    for (const nom of variablesDe(condition)) {
        if (!connues.has(nom)) {
            throw new ErreurCatalogue(`${ou} : la condition utilise « ${nom} », qui n'est ni une caractéristique déclarée ni une variable calculée`);
        }
    }
}
/** Variables calculées, disponibles dans les conditions en plus des caractéristiques. */
export const VARIABLES_CALCULEES = ['anciennete_ans', 'entretien_connu'];
export function valideCatalogueEquipements(brut) {
    const cat = brut;
    if (!cat || !Array.isArray(cat.types))
        throw new ErreurCatalogue('catalogue équipements : « types » attendu');
    const vues = new Set();
    for (const type of cat.types) {
        const ou = `type « ${String(type?.cle)} »`;
        exigeCle(type.cle, 'catalogue équipements');
        if (vues.has(type.cle))
            throw new ErreurCatalogue(`${ou} : clé en doublon`);
        vues.add(type.cle);
        exigeTexte(type.nom, ou);
        exigeTexte(type.famille, ou);
        exigeCategorie(type.categorie, ou);
        const connues = new Set(VARIABLES_CALCULEES);
        const clesCaracteristiques = new Set();
        for (const c of type.caracteristiques ?? []) {
            exigeCle(c.cle, `${ou}, caractéristique`);
            exigeTexte(c.libelle, `${ou}, caractéristique « ${c.cle} »`);
            if (!TYPES_CARACTERISTIQUE.includes(c.type)) {
                throw new ErreurCatalogue(`${ou}, caractéristique « ${c.cle} » : type inconnu « ${c.type} »`);
            }
            if (c.type === 'choix' && (!Array.isArray(c.choix) || c.choix.length === 0)) {
                throw new ErreurCatalogue(`${ou}, caractéristique « ${c.cle} » : liste de choix manquante`);
            }
            connues.add(c.cle);
            clesCaracteristiques.add(c.cle);
        }
        const clesRegles = new Set();
        for (const r of type.regles ?? []) {
            const ouR = `${ou}, règle « ${String(r?.cle)} »`;
            exigeCle(r.cle, ou);
            if (clesRegles.has(r.cle))
                throw new ErreurCatalogue(`${ouR} : clé en doublon`);
            clesRegles.add(r.cle);
            exigeTexte(r.titre, ouR);
            if (!ORIGINES.includes(r.origine))
                throw new ErreurCatalogue(`${ouR} : origine inconnue « ${r.origine} »`);
            // Une obligation légale sans texte de référence n'est pas affichable :
            // le badge « ⚖ » promet de citer la source.
            if (r.origine === 'legale' && !r.reference) {
                throw new ErreurCatalogue(`${ouR} : une obligation légale doit citer sa référence`);
            }
            if (!AMORCES.includes(r.depuis))
                throw new ErreurCatalogue(`${ouR} : amorce inconnue « ${r.depuis} »`);
            if (r.base !== 'echeance' && r.base !== 'realisation') {
                throw new ErreurCatalogue(`${ouR} : base inconnue « ${r.base} »`);
            }
            if (typeof r.defaut !== 'boolean')
                throw new ErreurCatalogue(`${ouR} : « defaut » booléen requis`);
            exigeRappels(r.rappels, ouR);
            if (r.rrule)
                valideRrule(r.rrule, ouR, clesCaracteristiques);
            if (!r.rrule && r.ansApres === undefined) {
                throw new ErreurCatalogue(`${ouR} : une règle sans rrule doit préciser « ansApres »`);
            }
            if (r.condition)
                valideCondition(r.condition, ouR, connues);
            if (r.categorie)
                exigeCategorie(r.categorie, ouR);
        }
        if (!type.libre && clesRegles.size === 0 && !type.garantieLegaleAns && !type.garantieConstructeurAns) {
            throw new ErreurCatalogue(`${ou} : aucune règle et aucune garantie — marquer « libre » si c'est voulu`);
        }
    }
    return cat;
}
export function valideCatalogueSante(brut) {
    const cat = brut;
    if (!cat || !Array.isArray(cat.jalons))
        throw new ErreurCatalogue('catalogue santé : « jalons » attendu');
    if (!cat.avertissement) {
        throw new ErreurCatalogue('catalogue santé : « avertissement » requis — l’interface doit rappeler que ces données ne remplacent pas l’avis du médecin');
    }
    const vues = new Set();
    for (const j of cat.jalons) {
        const ou = `jalon santé « ${String(j?.cle)} »`;
        exigeCle(j.cle, 'catalogue santé');
        if (vues.has(j.cle))
            throw new ErreurCatalogue(`${ou} : clé en doublon`);
        vues.add(j.cle);
        exigeTexte(j.titre, ou);
        if (!Number.isInteger(j.aAgeMois) || j.aAgeMois < 0) {
            throw new ErreurCatalogue(`${ou} : « aAgeMois » entier positif requis`);
        }
        if (j.jusquaAgeMois !== undefined && j.jusquaAgeMois <= j.aAgeMois) {
            throw new ErreurCatalogue(`${ou} : « jusquaAgeMois » doit dépasser « aAgeMois »`);
        }
        if (j.jusquaAgeMois !== undefined && !j.rrule) {
            throw new ErreurCatalogue(`${ou} : « jusquaAgeMois » n'a de sens qu'avec une rrule`);
        }
        if (!ORIGINES_SANTE.includes(j.origine))
            throw new ErreurCatalogue(`${ou} : origine inconnue`);
        if (j.rrule)
            valideRrule(j.rrule, ou, new Set());
        if (j.sexe && j.sexe !== 'F' && j.sexe !== 'M')
            throw new ErreurCatalogue(`${ou} : sexe attendu « F » ou « M »`);
        if (j.rappels)
            exigeRappels(j.rappels, ou);
    }
    return cat;
}
export function valideCatalogueEcole(brut) {
    const cat = brut;
    if (!cat || !Array.isArray(cat.regles))
        throw new ErreurCatalogue('catalogue école : « regles » attendu');
    const vues = new Set();
    for (const r of cat.regles) {
        const ou = `règle école « ${String(r?.cle)} »`;
        exigeCle(r.cle, 'catalogue école');
        if (vues.has(r.cle))
            throw new ErreurCatalogue(`${ou} : clé en doublon`);
        vues.add(r.cle);
        exigeTexte(r.titre, ou);
        exigeCategorie(r.categorie, ou);
        if (!ORIGINES.includes(r.origine))
            throw new ErreurCatalogue(`${ou} : origine inconnue`);
        if (r.origine === 'legale' && !r.reference) {
            throw new ErreurCatalogue(`${ou} : une obligation légale doit citer sa référence`);
        }
        if (typeof r.defaut !== 'boolean')
            throw new ErreurCatalogue(`${ou} : « defaut » booléen requis`);
        exigeRappels(r.rappels, ou);
        // Un ancrage et une RRULE se disputeraient la même échéance.
        if (!r.ancrage === !r.rrule) {
            throw new ErreurCatalogue(`${ou} : préciser soit « ancrage », soit « rrule », mais pas les deux`);
        }
        if (r.ancrage && !ANCRAGES.includes(r.ancrage)) {
            throw new ErreurCatalogue(`${ou} : ancrage inconnu « ${r.ancrage} »`);
        }
        if (r.rrule)
            valideRrule(r.rrule, ou, new Set());
    }
    return cat;
}
export function valideCatalogueAdministratif(brut) {
    const cat = brut;
    if (!cat || !Array.isArray(cat.echeancier) || !Array.isArray(cat.modeles)) {
        throw new ErreurCatalogue('catalogue administratif : « echeancier » et « modeles » attendus');
    }
    const vues = new Set();
    for (const e of cat.echeancier) {
        const ou = `échéance administrative « ${String(e?.cle)} »`;
        exigeCle(e.cle, 'catalogue administratif');
        if (vues.has(e.cle))
            throw new ErreurCatalogue(`${ou} : clé en doublon`);
        vues.add(e.cle);
        exigeTexte(e.titre, ou);
        exigeCategorie(e.categorie, ou);
        if (!ORIGINES.includes(e.origine))
            throw new ErreurCatalogue(`${ou} : origine inconnue`);
        if (e.origine === 'legale' && !e.reference) {
            throw new ErreurCatalogue(`${ou} : une obligation légale doit citer sa référence`);
        }
        if (typeof e.aConfirmer !== 'boolean')
            throw new ErreurCatalogue(`${ou} : « aConfirmer » booléen requis`);
        if (typeof e.defaut !== 'boolean')
            throw new ErreurCatalogue(`${ou} : « defaut » booléen requis`);
        exigeRappels(e.rappels, ou);
        valideRrule(e.rrule, ou, new Set());
    }
    for (const m of cat.modeles) {
        const ou = `modèle administratif « ${String(m?.cle)} »`;
        exigeCle(m.cle, 'catalogue administratif');
        if (vues.has(m.cle))
            throw new ErreurCatalogue(`${ou} : clé en doublon`);
        vues.add(m.cle);
        exigeTexte(m.titre, ou);
        exigeCategorie(m.categorie, ou);
        if (m.parametre !== 'date_echeance' && m.parametre !== 'date_expiration') {
            throw new ErreurCatalogue(`${ou} : paramètre attendu « date_echeance » ou « date_expiration »`);
        }
        if (m.parametre === 'date_echeance' && m.preavisJours === undefined) {
            throw new ErreurCatalogue(`${ou} : un préavis de résiliation en jours est requis`);
        }
        if (m.parametre === 'date_expiration' && m.moisAvant === undefined) {
            throw new ErreurCatalogue(`${ou} : le nombre de mois d'avance est requis`);
        }
        exigeRappels(m.rappels, ou);
        if (m.rrule)
            valideRrule(m.rrule, ou, new Set());
    }
    return cat;
}
//# sourceMappingURL=catalogue.js.map